/** @module Auth0Localizer */
/* eslint-disable import/extensions */
import languagePacks from './auth0-language-packs.mjs';
import Auth0Utils from './auth0-utils-browser.mjs';
import Auth0Constants from './auth0-constants.mjs';
import Auth0DynamicScriptLoader from './auth0-dynamic-script-loader.mjs';
import logger from './logging/auth0-logger.mjs';
import Auth0ErrorTranslationLayer from './auth0-error-translation-layer.mjs';
import Auth0CdnResourceResolver from '../cdn/utils/auth0-cdn-resource-resolver.mst.mjs';

const languageContextMenuId = 'language-context-menu';
const defaultLanguage = 'en';

class Auth0Localizer {
  constructor() {
    this.initLocalization();
  }

  initLocalization = () => {
    const userLanguage = navigator.language;
    const userLanguageNoDialect = userLanguage.slice(0, 2);
    // language selection order or precedence
    // 1. A previous user selection stored in a cookie
    // 2. The browsers navigator.language prop (e.g. `fr-CA`)
    // 3. The browsers navigator.language prop base language (e.g. for `fr-CA`, check for `fr`)
    // 4. Use default language
    const localeOption = Auth0Utils.getOption({ keyName: Auth0Constants.AUTH_LOCALE_KEY }, false);

    console.log(`locale state => userLanguage: ${userLanguage}; userLanguageNoDialect: ${userLanguageNoDialect}, localeOption: ${localeOption}`);

    if (localeOption && languagePacks[localeOption]) {
      console.log(`Auth0Localizer found stored locale option => ${localeOption}`);
      this._currentLanguageKey = localeOption;
    } else if (languagePacks[userLanguage]) {
      this._currentLanguageKey = userLanguage;
      console.log(`Auth0Localizer using browser specified locale => ${userLanguage}`);
    } else if (languagePacks[userLanguageNoDialect]) {
      this._currentLanguageKey = userLanguageNoDialect;
      console.log(`Auth0Localizer using browser specified locale without dialect specifier => ${userLanguageNoDialect}`);
    } else {
      this._currentLanguageKey = defaultLanguage;
      console.log(`Auth0Localizer using default locale => ${defaultLanguage}`);
    }
    document.documentElement.setAttribute('lang', this._currentLanguageKey);

    console.log(`Auth0Localizer initialized to '${this._currentLanguageKey}'; ${Object.keys(languagePacks).length} language packs found (${Object.keys(languagePacks).join(', ')})...`);
    // console.log(`pack dump => ${JSON.stringify(this.userLanguagePack)}`);
  };

  get userLanguage() {
    return this._currentLanguageKey;
  }

  get userLanguagePack() {
    return languagePacks[this._currentLanguageKey];
  }

  get defaultUserLanguagePack() {
    return languagePacks[defaultLanguage];
  }

  initLocalizationContextMenu = (options) => {
    options.contextMenuLinkIds = (typeof options.contextMenuLinkIds === 'string') ? [options.contextMenuLinkIds] : options.contextMenuLinkIds;
    this._currentLocaleIndicatorId = options.currentLocaleIndicatorId || 'language-code';

    this._addLanguageContextMenuToDoc(options);

    const languageContextHandler = (event) => {
      event.preventDefault(); // Prevent default link behavior

      if (this._isLocalContextMenuShowing()) {
        // unsure why, but on same pages the click event fires twice and effectively shows then immediately hides dropdown
        // this isn't really needed tho
        // this._hideLocaleContextMenu();
        return;
      }

      this._showLocaleContextMenu();

      const xOffset = options?.xOffset ?? -145;
      const yOffset = options?.yOffset ?? 18;
      // Position the context menu relative to the clicked link
      this._localeContextMenuElement.style.top = `${event.pageY + yOffset}px`;
      this._localeContextMenuElement.style.left = `${event.pageX + xOffset}px`;
    };

    const elemArray = options.contextMenuLinkIds.map(id => document.getElementById(id));
    elemArray.forEach((contextMenuLink) => {
      console.log(`Adding language context menu for link ${contextMenuLink}`);
      contextMenuLink.addEventListener('click', languageContextHandler);
    });

    // Hide the context menu when clicking outside of it
    document.addEventListener('click', (event) => {
      if (!this._localeContextMenuElement.contains(event.target) && !elemArray.some(e => e === event.target)) {
        this._hideLocaleContextMenu();
      }
    });

    this._updateCurrentLocaleIndicator();
  };

  _buildLanguageMenuItem = (langPack, flagIconsUrl) => {
    // New flags (open src svgs on github) can be found here: https://flagicons.lipis.dev/
    flagIconsUrl = !flagIconsUrl.endsWith('/') ? `${flagIconsUrl}/` : flagIconsUrl;
    return `<img class="country-flag" width="23" src="${flagIconsUrl}${langPack.flagIcon}.svg" /> ${langPack.displayName}`;
  };

  _addLanguageContextMenuToDoc = (options) => {
    if (document.getElementById(languageContextMenuId)) return;

    const menuEl = document.createElement('div');
    menuEl.id = languageContextMenuId;
    menuEl.style.display = 'none';
    // requires auth0-default-styles.css
    menuEl.classList.add('context-menu');
    const ul = document.createElement('ul');

    Object.keys(languagePacks).forEach((langKey) => {
      const ulItem = document.createElement('li');
      const ulItemAnchor = document.createElement('a');
      ulItem.onclick = this._changeLocaleHandler;
      ulItemAnchor.dataset.langCode = langKey;
      ulItemAnchor.innerHTML = this._buildLanguageMenuItem(languagePacks[langKey], options.flagIconsUrl);
      ulItem.appendChild(ulItemAnchor);
      ul.appendChild(ulItem);
    });

    menuEl.appendChild(ul);
    document.body.appendChild(menuEl);

    this._localeContextMenuElement = menuEl;
  };

  _isLocalContextMenuShowing = () => {
    return this._localeContextMenuElement.style.display !== 'none';
  };

  _showLocaleContextMenu = () => {
    this._localeContextMenuElement.style.display = 'block';
  };

  _hideLocaleContextMenu = () => {
    this._localeContextMenuElement.style.display = 'none';
  };

  _updateCurrentLocaleIndicator = () => {
    console.log(`_updateCurrentLocaleIndicator => set ${this._currentLocaleIndicatorId} to ${this.userLanguage.toUpperCase()}`);
    const ele = document.getElementById(this._currentLocaleIndicatorId);
    ele.innerText = this.userLanguage.toUpperCase();
  };

  _safeResourceLookup = (resourceId) => {
    return this.userLanguagePack[resourceId] || this.defaultUserLanguagePack[resourceId];
  };

  _changeLocaleHandler = (event) => {
    console.log(`Changing locales to ${event.target.dataset.langCode}; current is ${this.userLanguage}`);
    this._hideLocaleContextMenu();
    this._currentLanguageKey = languagePacks[event.target.dataset.langCode]
      ? event.target.dataset.langCode
      : defaultLanguage;
    this._updateCurrentLocaleIndicator();
    if (this._currentLanguageKey !== event.target.dataset.langCode) {
      logger.warn(`Local ${event.target.dataset.langCode} was selected but does not exist. Using ${defaultLanguage}`);
    }
    Auth0Utils.setOption({
      cookieName: Auth0Constants.AUTH_LOCALE_KEY,
      value: this._currentLanguageKey,
      expiryDay: 365,
    });
    document.documentElement.setAttribute('lang', this._currentLanguageKey);
    this.updateTranslatablePageElements();
  };

  getLocalizedText = (forKey, replacements = null) => {
    console.log(`getLocalizedText => ${forKey}`);
    const localizedObj = this._safeResourceLookup(forKey);
    let txt = localizedObj ? localizedObj.defaultMessage : forKey;

    if (Array.isArray(replacements)) {
      for (let i = 0; i < replacements.length; i++) {
        txt = txt.replaceAll(`{${i}}`, replacements[i]);
      }
    } else if (replacements && typeof replacements === 'string') {
      txt = txt.replaceAll('{0}', replacements);
    } else if (replacements && typeof replacements === 'object') {
      Object.keys(replacements).forEach((replacerKey) => {
        txt = txt.replaceAll(`{${replacerKey}}`, replacements[replacerKey]);
      });
    }
    return txt;
  };

  enableMarkdownConversion = async () => {
    if (this._convertMarkdown) return;

    if (!window.markdownit) {
      const scriptLoader = new Auth0DynamicScriptLoader();
      await scriptLoader.loadScripts({ url: Auth0CdnResourceResolver.AUTH0_SCRIPT_URL_MARKDOWN_IT, required: false });
    }
    if (!window.markdownit) {
      logger.error('Unable to enable markdown conversion. Failed to load dependency scripts.');
      return;
    }
    this._convertMarkdown = window.markdownit();
    console.log('Auth0Localizer => markdown 2 HTML conversion initialized...');
  };

  setElementLocalizedText = async (elementData, replacements = null) => {
    console.log(`setElementLocalizedText => ${JSON.stringify(elementData)}`);
    const elementId = typeof elementData === 'string'
      ? elementData
      : elementData.id;
    const resourceId = typeof elementData === 'string'
      ? elementId
      : (elementData?.resourceIdOverride || elementId);

    const resourceEntry = this._safeResourceLookup(resourceId);
    const resourceText = this.getLocalizedText(resourceId, replacements);
    const ele = document.getElementById(elementId);
    if (ele) {
      if (!resourceEntry) {
        // untranslatable resource, just render as is...
        ele.innerHTML = this._convertMarkdown ? this._convertMarkdown.renderInline(resourceText) : resourceText;
      } else if (resourceEntry.type === 'markdown') {
        ele.innerHTML = this._convertMarkdown ? this._convertMarkdown.renderInline(resourceText) : resourceText;
      } else {
        ele.innerText = resourceText;
      }
      ele.setAttribute(`data-${Auth0Constants.AUTH_LOCALE_TRANSLATABLE_KEY}`, true);
      if (typeof elementData !== 'string' && elementData.resourceIdOverride) {
        ele.setAttribute(`data-${Auth0Constants.AUTH_LOCALE_RESOURCE_ID_KEY}`, resourceId);
      }
      if (replacements) {
        ele.setAttribute(`data-${Auth0Constants.AUTH_LOCALE_REPLACEMENT_ARGS_KEY}`, JSON.stringify(replacements));
      }
    }
  };

  updateTranslatablePageElements = async () => {
    // console.log(`updateTranslatablePageElements for ${window.location} using language pack ${this._currentLanguageKey}...`);
    const translatableElements = document.querySelectorAll(`[data-${Auth0Constants.AUTH_LOCALE_TRANSLATABLE_KEY}]`);

    if (!translatableElements || !translatableElements.length) return;

    translatableElements.forEach((ele) => {
      // console.log(`process ele ${ele.id}`);
      const resourceId = ele.getAttribute(`data-${Auth0Constants.AUTH_LOCALE_RESOURCE_ID_KEY}`) || ele.id;
      // console.log(`using resource id ${resourceId}`);
      const resourceEntry = this._safeResourceLookup(resourceId);
      const translationArgsRaw = ele.getAttribute(`data-${Auth0Constants.AUTH_LOCALE_REPLACEMENT_ARGS_KEY}`);
      const translationArgs = translationArgsRaw
        ? JSON.parse(translationArgsRaw)
        : null;
      if (translationArgs) {
        console.log(`using translation args ${JSON.stringify(translationArgs)}`);
      } else {
        console.log('no translation args found');
      }
      const message = this.getLocalizedText(resourceId, translationArgs);
      // console.log(`with message ${message}`);
      if (ele) {
        if (resourceEntry.type === 'markdown') {
          ele.innerHTML = this._convertMarkdown ? this._convertMarkdown.renderInline(message) : message;
        } else {
          ele.innerText = message;
        }
      }
    });
  };

  displayLocalizedError = (err, errReplacementArgs = null) => {
    // IMPORTANT: current document must have an element with ID `error-message`
    this._displayLocalizedNotification(err, 'error', errReplacementArgs);
  };

  displayLocalizedInfo = (info, infoReplacementArgs = null) => {
    // IMPORTANT: current document must have an element with ID `info-message`
    this._displayLocalizedNotification(info, 'info', infoReplacementArgs);
  };

  _displayLocalizedNotification = (msgKey, msgType, msgReplacementArgs = null) => {
    console.log(`_displayLocalizedNotification=>${msgKey}`);
    msgKey = this.resourceKeyFromMessage(msgKey);
    this.setElementLocalizedText({ id: `${msgType}-message`, resourceIdOverride: msgKey }, msgReplacementArgs);
    const msgContainer = document.getElementById(`${msgType}-message`);
    msgContainer.style.display = 'block';
  };

  isValidLocalizationKey = (key) => {
    return this.defaultUserLanguagePack[key];
  };

  _extractLocalizationKeyFromMessage = (msg) => {
    console.log(`_extractLocalizationKeyFromMessage > ${JSON.stringify(msg)}`);
    // TODO: sigh... need to go thru and standardize this to we can kill this
    const msgIsString = typeof msg === 'string';
    const msgIsObject = typeof msg === 'object';
    const maybeKey = msgIsObject && !msgIsString
      ? msg.key || msg.message || msg.description || msg.error
      : msg;

    return this.isValidLocalizationKey(maybeKey)
      ? maybeKey
      : null;
  };

  extractMessageTextFromError = (err) => {
    console.log(`extractMessageTextFromError > ${JSON.stringify(err)}`);
    const msgIsString = typeof err === 'string';
    const msgIsObject = typeof err === 'object';
    return msgIsObject && !msgIsString
      ? err.message || err.description || err.error || err.error_description
      : err;
  };

  resourceKeyFromMessage = (err) => {
    console.log(`resourceKeyFromMessage > ${JSON.stringify(err)}`);
    const localizationKey = this._extractLocalizationKeyFromMessage(err);
    if (localizationKey) {
      return localizationKey;
    }

    // no localization key found so see if it a pattern match to known errors
    const converted = Auth0ErrorTranslationLayer.convert(err);
    if (converted.matched) {
      return converted.key;
    }

    // give up... just display what we have
    return (err instanceof Error)
      ? err.message
      : err;
  };
}

export default new Auth0Localizer();
